/**
 * ONE answer to "where does this device live".
 *
 * A device address is stored as a bare host that MAY carry its own port:
 * "192.168.1.50" on the LAN, "hikddns.com:1020" over DDNS. Two parsers disagreed about
 * that embedded port — the Hikvision adapter honoured it, while the hand-built
 * face-delete URL in deviceCommunication took `device.port || '80'`. Over DDNS the
 * delete therefore went to port 80 while the upload went to 1020: the delete failed, its
 * error was swallowed, and the upload hit `deviceUserAlreadyExistFace`, so a new photo
 * never replaced the old one.
 *
 * Every device URL must be built from here so that can't drift again.
 */

/**
 * Only the address fields are needed, so BOTH `DeviceInfo` shapes in the codebase
 * (services/deviceCommunication and adapters/base, which differ on `vendor`) satisfy it.
 */
export interface AddressableDevice {
  id?: string;
  ip_address?: string;
  local_ip_address?: string;
  /** Typed string across the codebase, but the API and some callers hand over a number. */
  port?: string | number;
}

export interface DeviceHostPort {
  host: string;
  port: string;
  protocol: 'http' | 'https';
}

/**
 * Resolves a device's host, port and scheme.
 *
 * Precedence, matching what the adapter already did: the PUBLIC address wins over the
 * local one, and a port embedded in that address wins over the `port` field — the
 * embedded port is part of how the operator wrote the address, so it is the more
 * specific statement.
 */
export function resolveDeviceHostPort(device: AddressableDevice): DeviceHostPort {
  const raw = (device?.ip_address || device?.local_ip_address || '').trim();
  if (!raw) {
    throw new Error(`Device ${device?.id ?? '(unknown)'} has no address configured`);
  }

  const withoutScheme = raw.replace(/^https?:\/\//i, '');
  const [hostPart, embeddedPort] = withoutScheme.split(':');

  const host = hostPart.trim();
  if (!host) {
    throw new Error(`Device ${device?.id ?? '(unknown)'} has no address configured`);
  }

  const port = String(embeddedPort || device?.port || '80').trim();
  return { host, port, protocol: port === '443' ? 'https' : 'http' };
}

/** Origin for a device, plus an optional path. Use this instead of string-building a URL. */
export function deviceBaseUrl(device: AddressableDevice, path = ''): string {
  const { host, port, protocol } = resolveDeviceHostPort(device);
  const origin = `${protocol}://${host}:${port}`;
  if (!path) return origin;
  return `${origin}${path.startsWith('/') ? '' : '/'}${path}`;
}
